import React from 'react';
import { Helmet } from 'react-helmet';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import glossaryData from '../content/glossary.json';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { PageSection } from 'apps/sigma2-ui/src/components/PageSection';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';

interface DefinitionBlockProps {
  term: string;
  phoneticSpelling: string;
  wordClass: string;
  definition: string;
  example?: string;
}
interface LetterBlockProps {
  letter: string;
  terms: DefinitionBlockProps[];
}

const DefinitionBlock = ({
  term,
  phoneticSpelling,
  wordClass,
  definition,
  example,
}: DefinitionBlockProps) => {
  return (
    <div>
      <dl
        className="font-sans"
        data-qa={`termBlock_${term.replace(/\s/g, '')}`}
      >
        <div className="flex items-baseline">
          <dt className="text-gray-700 text-lg pr-1 font-serif" data-qa="term">
            {term}
          </dt>
          <span className="text-gray-1000 text-lg pr-1">·</span>
          <dd className="text-gray-600 text-base" data-qa="wordClass">
            {wordClass}
          </dd>
        </div>
        <dd className="text-gray-600 text-base mb-2" data-qa="phoneticSpelling">
          {phoneticSpelling}
        </dd>
        <dd className="text-gray-800 text-sm" data-qa="definition">
          {definition}
        </dd>
        {example ? (
          <dd className="text-gray-800 text-sm mt-4 italic" data-qa="example">
            <span className="not-italic">Example:</span> {example}
          </dd>
        ) : (
          ''
        )}
      </dl>
    </div>
  );
};

const LetterBlock = ({ letter, terms }: LetterBlockProps) => {
  return (
    <section id={letter} data-qa={`letterBlock_${letter}`}>
      <Heading level={2} textColor="text-gray-800">
        {letter}
      </Heading>
      <div
        className="grid grid-cols-2 gap-6"
        data-qa={`definitionBlock_${letter}`}
      >
        {terms.map((term, i) => (
          <DefinitionBlock {...term} key={i} />
        ))}
      </div>
      <hr className="mt-6  mb-6 bg-gray-400 h-0.5" />
    </section>
  );
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(/\W?/);

const lettersInGlossary = glossaryData.map(
  (glossaryLetter) => glossaryLetter.letter,
);

export const GlossaryPage = () => {
  const language = useLanguage();
  useAuthenticatedPageRedirect();

  return (
    <MainLayout showBackToTop>
      <Helmet>
        <title>{documentTitle(language.glossary())}</title>
      </Helmet>
      <PageSection>
        <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

        <div className="col-span-full space-y-4">
          <Heading level={1}>{language.glossaryHeading()}</Heading>
          <p
            className="text-sm text-gray-600 font-sans mb-6 -mt-2"
            data-qa="glossaryBlurb"
          >
            {language.glossaryInstructionsBlurb()}
          </p>
          <p
            className="text-sm text-gray-600 font-sans mb-6 -mt-2"
            data-qa="glossaryBlurb"
          >
            {language.glossaryPraticipateBlurb()}
          </p>
          <ul className="flex text-sm mt-6 mb-6 text-gray-600 justify-start">
            {ALPHABET.map((letter, i) => (
              <li className="pr-1 text-base" key={i}>
                {lettersInGlossary.includes(letter) ? (
                  <a
                    data-qa={`letterElem_${letter}`}
                    href={`#${letter}`}
                    onClick={(e) => {
                      e.preventDefault();

                      document
                        .getElementById(letter)
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="underline text-tertiary"
                  >
                    {letter}
                  </a>
                ) : (
                  <span data-qa={`letterElem_${letter}`}>{letter}</span>
                )}
                {i < 25 ? <span className="pl-1 text-base">•</span> : ''}
              </li>
            ))}
          </ul>
          <hr className="mt-6  mb-6 bg-gray-400 h-0.5" />
          {glossaryData.map((letterBlockProps: LetterBlockProps, i) => (
            <LetterBlock {...letterBlockProps} key={i} />
          ))}
        </div>
      </PageSection>
    </MainLayout>
  );
};
